import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useHasLiked, useLikes } from '@/hooks/useLikes';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import type { NostrEvent } from '@nostrify/nostrify';
import { useNostr } from '@nostrify/react';
import { useToast } from '@/hooks/useToast';

interface LikeButtonProps {
  event: NostrEvent;
  size?: 'default' | 'sm' | 'lg' | 'icon';
  variant?: 'default' | 'ghost' | 'outline';
  className?: string;
  showCount?: boolean;
}

export function LikeButton({
  event,
  size = 'default',
  variant = 'ghost',
  className,
  showCount = false,
}: LikeButtonProps) {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutate: publishEvent } = useNostrPublish();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: likes } = useLikes(event.id);
  const hasLiked = useHasLiked(event.id, user?.pubkey);

  const likeCount = likes?.length || 0;

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast({
        title: 'Login required',
        description: 'You need to log in to like tracks',
        variant: 'destructive',
      });
      return;
    }

    if (hasLiked) {
      // Unlike: find and delete the user's like reaction
      const userLike = likes?.find((like) => like.pubkey === user.pubkey);
      if (userLike) {
        try {
          // Publish a deletion event (kind 5)
          publishEvent({
            kind: 5,
            content: '',
            tags: [['e', userLike.id]],
          });

          // Optimistically update the likes cache
          queryClient.setQueryData(['likes', event.id], (old: NostrEvent[] | undefined) => {
            if (!old) return [];
            return old.filter((like) => like.id !== userLike.id);
          });

          // Immediately invalidate and refetch liked events
          await queryClient.invalidateQueries({
            queryKey: ['liked-events', user.pubkey],
            refetchType: 'active'
          });

          toast({
            title: 'Unliked',
            description: 'Removed from your likes',
          });
        } catch (error) {
          console.error('Failed to unlike:', error);
          toast({
            title: 'Error',
            description: 'Failed to unlike',
            variant: 'destructive',
          });
        }
      }
    } else {
      // Like: create a new reaction
      try {
        // Get the 'd' tag for addressable events
        const dTag = event.tags.find((t) => t[0] === 'd')?.[1];

        const tags: string[][] = [
          ['e', event.id],
          ['p', event.pubkey],
          ['k', String(event.kind)],
        ];

        // For addressable events, also include 'a' tag
        if (dTag && event.kind >= 30000 && event.kind < 40000) {
          tags.push(['a', `${event.kind}:${event.pubkey}:${dTag}`]);
        }

        publishEvent({
          kind: 7,
          content: '❤️',
          tags,
        });

        // Optimistically add to likes cache
        queryClient.setQueryData(['likes', event.id], (old: NostrEvent[] | undefined) => {
          const optimisticLike: NostrEvent = {
            id: 'temp-' + Date.now(),
            pubkey: user.pubkey,
            created_at: Math.floor(Date.now() / 1000),
            kind: 7,
            tags,
            content: '❤️',
            sig: '',
          };
          return [...(old || []), optimisticLike];
        });

        // Immediately invalidate and refetch liked events
        await queryClient.invalidateQueries({
          queryKey: ['liked-events', user.pubkey],
          refetchType: 'active'
        });

        toast({
          title: 'Liked! ❤️',
          description: 'Added to your liked collection',
        });
      } catch (error) {
        console.error('Failed to like:', error);
        toast({
          title: 'Error',
          description: 'Failed to like',
          variant: 'destructive',
        });
      }
    }

    // Refetch likes after a short delay to get actual data from relays
    setTimeout(async () => {
      await queryClient.invalidateQueries({
        queryKey: ['likes', event.id],
        refetchType: 'active'
      });
      await queryClient.invalidateQueries({
        queryKey: ['liked-events', user.pubkey],
        refetchType: 'active'
      });
    }, 1500);
  };

  return (
    <Button
      size={size}
      variant={variant}
      onClick={handleLike}
      className={cn(
        'rounded-full transition-colors',
        hasLiked && 'text-red-500 hover:text-red-600',
        className
      )}
      title={hasLiked ? 'Unlike' : 'Like'}
    >
      <Heart className={cn('h-4 w-4', hasLiked && 'fill-current')} />
      {showCount && likeCount > 0 && (
        <span className="ml-1 text-xs font-semibold">{likeCount}</span>
      )}
    </Button>
  );
}
